import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProjects, useDeleteProject } from '../hooks/useProject'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'

interface Project {
  id: number
  name: string
  location?: string
  status: 'draft' | 'active' | 'completed'
  startDate?: string
  endDate?: string
  totalValue?: number
  maintenanceDuration?: number
}

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate()
  const { data: projects, isLoading, error } = useProjects()
  const deleteProjectMutation = useDeleteProject()
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'active' | 'completed'>('all')

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading projects: {error.message}</p>
        </div>
      </div>
    )
  }

  const filteredProjects = projects?.filter(project =>
    filterStatus === 'all' || project.status === filterStatus
  ) || []

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-gray-600 mt-1">Manage and view all your projects</p>
          </div>
          <button
            onClick={() => navigate('/projects/new')}
            className="btn-primary"
          >
            New Project
          </button>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({projects?.length || 0})
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'draft'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Draft ({projects?.filter(p => p.status === 'draft').length || 0})
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Active ({projects?.filter(p => p.status === 'active').length || 0})
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'completed'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Completed ({projects?.filter(p => p.status === 'completed').length || 0})
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center shadow-sm">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
          <p className="text-gray-500 mb-4">
            {filterStatus === 'all'
              ? "Get started by creating your first project."
              : `No projects with status "${filterStatus}".`
            }
          </p>
          <button
            onClick={() => navigate('/projects/new')}
            className="btn-primary"
          >
            Create Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/projects/${project.id}/dashboard`)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {project.location || 'No location specified'}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                  {project.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex justify-between">
                  <span>Location:</span>
                  <span>{project.location || 'Not specified'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Start Date:</span>
                  <span>{formatDate(project.startDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span>End Date:</span>
                  <span>{formatDate(project.endDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Value:</span>
                  <span>{project.totalValue ? `Rp ${project.totalValue.toLocaleString()}` : 'Not set'}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/projects/${project.id}/dashboard`)
                    }}
                    className="flex-1 btn-secondary text-xs py-1"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/projects/${project.id}/boq`)
                    }}
                    className="flex-1 btn-secondary text-xs py-1"
                  >
                    BoQ
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
                        deleteProjectMutation.mutate(project.id)
                      }
                    }}
                    disabled={deleteProjectMutation.isPending}
                    className="btn-secondary text-xs py-1 px-2"
                  >
                    {deleteProjectMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProjectsPage